import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
// material
import { styled } from '@mui/material/styles';
import {
  Stack,
  Typography,
  TextField,
  Box,
  Alert,
  Button,
  Grid,
  Divider,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CheckCircle } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
// component
import Logo from '../../components/Logo';
import { useNotificationsProvider } from '../../context/NotificationsContext';
import { selectUser } from '../login/selectors';
import * as CognitoAuthService from '../../services/CognitoAuthService';

// ----------------------------------------------------------------------
const PageStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

const ContainerStyle = styled(Box)(({ theme }) => ({
  shadowColor: theme.palette.secondary,
  backgroundColor: theme.palette.grey,
  shadowOpacity: 0.8,
  shadowRadius: 2,
  shadowOffset: {
    height: 1,
    width: 1
  },
  elevation: 5,
}));

const LogoStyle = {
  width: 175,
  height: 48
};

const ISSUER = 'Betterfield';

// Cognito-flavoured 3-step setup. The previous flow had a "save recovery
// codes" step backed by a backend-side bcrypt list; Cognito doesn't expose
// recovery codes natively (the equivalent is an admin-driven MFA reset),
// so the step is dropped. If recovery codes are required again, generate
// them client-side and persist via a follow-up backend usecase.
export default function Setup2FA() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotificationsProvider();
  const user = useSelector(selectUser());

  const steps = [
    t('setup-2fa.step-welcome', 'Welcome'),
    t('setup-2fa.step-scan-qr', 'Scan QR Code'),
    t('setup-2fa.step-verify', 'Verify'),
  ];

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [secret, setSecret] = useState(null);
  const [verificationToken, setVerificationToken] = useState('');

  const handleSkip = () => {
    navigate('/dashboard/app');
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleSetup = async () => {
    setLoading(true);
    try {
      const { secretCode } = await CognitoAuthService.setupTOTP();
      setSecret(secretCode);
      setActiveStep(1);
    } catch (err) {
      showNotification('error', err?.message || t('2fa.setup-error', 'Failed to setup 2FA'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (!verificationToken || verificationToken.length !== 6) {
      showNotification('error', t('2fa.invalid-token', 'Please enter a 6-digit code'));
      return;
    }
    setLoading(true);
    try {
      await CognitoAuthService.verifyTOTP(verificationToken);
      setActiveStep(3); // success step (out of stepper range, intentional)
    } catch (err) {
      showNotification('error', err?.message || t('2fa.enable-error', 'Invalid code. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    navigate('/dashboard/app');
  };

  const otpauthUri = secret && user?.email
    ? `otpauth://totp/${encodeURIComponent(`${ISSUER}:${user.email}`)}?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}`
    : null;

  // Welcome Step
  const renderWelcomeStep = () => (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Typography variant="h4" gutterBottom>
          {t('setup-2fa.welcome-title', 'Secure Your Account')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('setup-2fa.welcome-description', 'Two-factor authentication (2FA) adds an extra layer of security to your account. When you log in, you\'ll need to enter a code from your authenticator app in addition to your password.')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('setup-2fa.welcome-benefits', 'Benefits of enabling 2FA:')}
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary">
            {t('setup-2fa.benefit-1', 'Protects your account even if your password is compromised')}
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            {t('setup-2fa.benefit-2', 'Prevents unauthorized access to your data')}
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            {t('setup-2fa.benefit-3', 'Industry-standard security practice')}
          </Typography>
        </Box>
      </Grid>
      <Grid item>
        <Alert severity="info">
          {t('setup-2fa.optional-notice', 'Setting up 2FA is optional, but highly recommended for security.')}
        </Alert>
      </Grid>
      <Grid item>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" fullWidth onClick={handleSkip}>
            {t('setup-2fa.skip', 'Skip for now')}
          </Button>
          <LoadingButton variant="contained" fullWidth onClick={handleSetup} loading={loading}>
            {t('setup-2fa.setup-button', 'Set up 2FA')}
          </LoadingButton>
        </Stack>
      </Grid>
    </Grid>
  );

  // QR Code Step
  const renderQRStep = () => (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Typography variant="h6">
          {t('2fa.scan-qr', 'Step 1: Scan QR Code')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('2fa.scan-description', 'Use Google Authenticator, Microsoft Authenticator, or any TOTP app to scan this QR code:')}
        </Typography>
      </Grid>
      {otpauthUri && (
        <Grid item container justifyContent="center">
          <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1, bgcolor: 'common.white' }}>
            <QRCodeSVG value={otpauthUri} size={220} includeMargin={false} />
          </Box>
        </Grid>
      )}
      <Grid item>
        <Typography variant="body2" color="text.secondary">
          {t('2fa.manual-entry', 'Or enter this code manually:')}
        </Typography>
        <TextField
          value={secret || ''}
          fullWidth
          InputProps={{ readOnly: true }}
          size="small"
          sx={{ mt: 1, fontFamily: 'monospace' }}
        />
      </Grid>
      <Grid item>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" fullWidth onClick={handleSkip}>
            {t('setup-2fa.skip', 'Skip for now')}
          </Button>
          <Button variant="contained" fullWidth onClick={handleNext}>
            {t('common.next', 'Next')}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );

  // Verification Step
  const renderVerificationStep = () => (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Typography variant="h6">
          {t('2fa.verify', 'Step 2: Verify')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('2fa.verify-description', 'Enter the 6-digit code from your authenticator app:')}
        </Typography>
      </Grid>
      <Grid item>
        <TextField
          type="text"
          value={verificationToken}
          onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
          label={t('2fa.verification-code', 'Verification Code')}
          fullWidth
          inputProps={{
            maxLength: 6,
            pattern: '[0-9]*',
            inputMode: 'numeric'
          }}
          helperText={t('2fa.verification-help', 'Enter the 6-digit code from your app')}
        />
      </Grid>
      <Grid item>
        <LoadingButton
          variant="contained"
          fullWidth
          onClick={handleEnable}
          loading={loading}
          disabled={verificationToken.length !== 6}
        >
          {t('2fa.enable-confirm', 'Enable 2FA')}
        </LoadingButton>
      </Grid>
    </Grid>
  );

  // Success Step
  const renderSuccessStep = () => (
    <Grid container direction="column" spacing={3}>
      <Grid item>
        <Alert severity="success" icon={<CheckCircle />}>
          {t('setup-2fa.success-title', '2FA Enabled Successfully!')}
        </Alert>
      </Grid>
      <Grid item>
        <Typography variant="body1" color="text.secondary">
          {t('setup-2fa.success-description', 'Your account is now protected with two-factor authentication. You\'ll need to enter a code from your authenticator app when logging in.')}
        </Typography>
      </Grid>
      <Grid item>
        <Button variant="contained" fullWidth onClick={handleFinish}>
          {t('setup-2fa.go-to-login', 'Continue to Login')}
        </Button>
      </Grid>
    </Grid>
  );

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return renderWelcomeStep();
      case 1:
        return renderQRStep();
      case 2:
        return renderVerificationStep();
      case 3:
        return renderSuccessStep();
      default:
        return null;
    }
  };

  return (
    <PageStyle>
      <ContainerStyle>
        <Stack spacing={3}>
          <Stack alignItems="center">
            <Logo sx={LogoStyle} />
          </Stack>

          {activeStep < 3 && (
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Divider />

          <Box sx={{ px: 2 }}>
            {renderStep()}
          </Box>
        </Stack>
      </ContainerStyle>
    </PageStyle>
  );
}
